import React, { forwardRef, useMemo } from 'react';
import { useI18n } from 'next-localization';

import clsx from 'clsx';
import { Controller, useForm, useFormContext } from 'react-hook-form';

import { NeuChangeEvent, NeuSearch } from '@hcahealthcaredpx/neutron-react';
import { InputSpinner } from '@xsf/shared-lib/components/input-spinner';
import { MIN_SEARCH_CHARACTERS, STORED_LOCATION_ID } from '@xsf/shared-lib/components/typeahead';

import { TypeaheadAriaResults } from './components/typeahead-aria-results';
import { TypeaheadNoResults } from './components/typeahead-no-results';
import { TypeaheadResults } from './components/typeahead-results';
import styles from './typeahead-new.module.scss';
import {
  isExternalTypeaheadProps,
  isRegexpValidationRule,
  NewTypeaheadProps,
  TypeaheadItem,
  TypeaheadSelectable,
} from './typeahead-new.types';

const DROPDOWN_SELECTOR = '.unit-dropdown';

export const TypeaheadNew = forwardRef<HTMLInputElement, NewTypeaheadProps>(
  ({ searchProps, fieldProps, typeaheadProps }, ref) => {
    const { initialSelectedItem, updateSelectedItemRef, trackTypeaheadQuery, autoSelectOnBlur } =
      typeaheadProps;

    const { t } = useI18n();

    // Initialize states
    const [selectedItem, setSelectedItem] = React.useState<TypeaheadItem | null>(null);
    const [typeaheadQuery, setTypeaheadQuery] = React.useState('');
    const [internalTypeaheadItems, setInternalTypeaheadItems] = React.useState<TypeaheadItem[]>([]);

    const typeaheadRef = React.useRef<HTMLInputElement>(null);
    const initialTypeaheadItemUsed = React.useRef<boolean>(false);

    // override the initial selected item only once
    React.useEffect(() => {
      if (initialSelectedItem && !initialTypeaheadItemUsed.current) {
        onSelectedItem(initialSelectedItem as TypeaheadSelectable);
        initialTypeaheadItemUsed.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSelectedItem]);

    // supporting refs for debounced query changes
    const latestQueryRef = React.useRef(typeaheadQuery);
    const isMountedRef = React.useRef(true);

    // check if component is mounted
    React.useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // decide how we manage typeahead items
    const manageItemsExternally = isExternalTypeaheadProps(typeaheadProps);
    const typeaheadItems = manageItemsExternally
      ? typeaheadProps.typeaheadResults
      : internalTypeaheadItems;

    // RHF context
    const internalFormContext = useForm({
      mode: 'onChange',
      defaultValues: {
        [fieldProps.name]: '',
      },
    });
    const externalFormContext = useFormContext();
    const formContext = externalFormContext || internalFormContext;

    const {
      control,
      watch,
      setValue,
      clearErrors,
      setError,
      formState: { errors },
    } = formContext;

    const fieldValue = watch(fieldProps.name);

    const internalValueClear = React.useRef(false);

    // if the field value is cleared externally, clear the typeahead query and selected item
    React.useEffect(() => {
      if (!fieldValue && typeaheadQuery !== '' && !internalValueClear.current) {
        setTypeaheadQuery('');
        setSelectedItem(null);
        clearErrors(fieldProps.name);
      }

      if (internalValueClear.current) internalValueClear.current = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldValue]);

    const rhfFieldError = errors[fieldProps.name as string];

    // check if the current query is the selected item or not
    const queryIsSelectedItem = React.useMemo(
      () =>
        selectedItem &&
        (typeaheadQuery === selectedItem.value ||
          typeaheadQuery === (selectedItem as TypeaheadSelectable).displayedValue),
      [selectedItem, typeaheadQuery]
    );

    // we should only show the dropdown if the query is min characters, is not the selected item, and not loading
    const shouldShowDropdown =
      typeaheadQuery.trim().length >= MIN_SEARCH_CHARACTERS &&
      !queryIsSelectedItem &&
      !typeaheadProps.typeaheadLoading &&
      errors[fieldProps.name as string] === undefined;

    const clearTypeaheadItems = React.useCallback(async () => {
      if (manageItemsExternally) {
        await typeaheadProps.triggerTypeaheadLookup('');
      } else {
        setInternalTypeaheadItems([]);
      }
    }, [manageItemsExternally, typeaheadProps]);

    // on selection we should let the consumer process the selection first, then set the selectedItem and update the query/value
    const onSelectedItem = async (item: TypeaheadSelectable) => {
      const processedItem = await typeaheadProps.processTypeaheadSelection?.(item);
      const finalItem = (processedItem || item) as TypeaheadSelectable;
      setSelectedItem(finalItem);
      setTypeaheadQuery(finalItem?.displayedValue || finalItem?.value || '');
      setValue(fieldProps.name, finalItem?.value || '');
    };

    // allow the parent to update the selected item with updateSelectedItemRef.current(item)
    React.useImperativeHandle(
      updateSelectedItemRef,
      () => (item: TypeaheadItem | null) => onSelectedItem(item as TypeaheadSelectable)
    );

    // effect to handle query changes, internally debounced to avoid excessive calls
    React.useEffect(() => {
      // notify the parent of query changes
      trackTypeaheadQuery?.(typeaheadQuery);

      latestQueryRef.current = typeaheadQuery;

      if (!queryIsSelectedItem && fieldProps.name !== '') {
        internalValueClear.current = true;
        setValue(fieldProps.name, '');
        setSelectedItem(null);
      }

      if (
        (typeaheadQuery.trim().length < MIN_SEARCH_CHARACTERS && typeaheadItems.length) ||
        queryIsSelectedItem
      ) {
        clearTypeaheadItems();
        return;
      }

      const updateTypeaheadItems = async () => {
        if (latestQueryRef.current !== typeaheadQuery || !isMountedRef.current) {
          return;
        }

        if (errors[fieldProps.name as string]) {
          return;
        }
        if (!manageItemsExternally) {
          const items = await typeaheadProps?.getTypeaheadItems(typeaheadQuery);
          setInternalTypeaheadItems(items || []);
        } else {
          await typeaheadProps.triggerTypeaheadLookup(typeaheadQuery);
        }
      };

      const timeoutId = setTimeout(updateTypeaheadItems, 300);
      return () => clearTimeout(timeoutId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeaheadQuery, queryIsSelectedItem]);

    // effect to trigger input event when dropdown visibility changes, workaround for NeuSearch dropdown not reacting to changes properly
    React.useEffect(() => {
      triggerNeutronReRender();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShowDropdown]);

    const triggerNeutronReRender = () => {
      if (
        typeaheadRef.current &&
        typeaheadRef.current instanceof HTMLInputElement &&
        typeaheadQuery
      ) {
        const event = new Event('input', { bubbles: true });
        typeaheadRef.current.dispatchEvent(event);
      }
    };

    const firstItem = useMemo(
      () => typeaheadItems.find((item): item is TypeaheadSelectable => item.type === 'item'),
      [typeaheadItems]
    );

    // Ref to focus first item in dropdown from input
    const focusFirstItemRef = React.useRef<(() => void) | null>(null);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle arrow keys to move focus to dropdown
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && shouldShowDropdown) {
        event.preventDefault();
        // Move focus to first item in dropdown
        focusFirstItemRef.current?.();
        return;
      }

      // Handle Tab key to auto-select first item when dropdown is visible
      if (event.key === 'Tab' && shouldShowDropdown && !selectedItem && firstItem) {
        event.preventDefault();
        onSelectedItem(firstItem);
      }
    };

    const handleBlur = (event: React.FocusEvent<Element, Element>) => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      const isMovingToDropdown =
        relatedTarget?.closest(`#${searchProps.id}-dropdown`) ||
        relatedTarget?.closest(DROPDOWN_SELECTOR);

      if (autoSelectOnBlur && !selectedItem && !isMovingToDropdown && firstItem) {
        onSelectedItem(firstItem);
      }

      fieldProps.onBlur?.(event as React.FocusEvent<HTMLInputElement, Element>);
    };

    const handleNeuChange = (e: NeuChangeEvent) => {
      clearErrors(fieldProps.name);

      // if its required and empty, set required error
      if (fieldProps.required && !e.value) {
        setError(fieldProps.name, { message: t('Search.Errors.Required') });
      }

      // check validations
      if (typeaheadProps.typeaheadValidationRules && e.value) {
        for (const rule of typeaheadProps.typeaheadValidationRules) {
          if (isRegexpValidationRule(rule)) {
            if (!rule.regex.test(e.value)) {
              setError(fieldProps.name, { message: rule.message });
              break;
            }
          } else {
            if (!rule.customValidator(e.value)) {
              setError(fieldProps.name, { message: rule.message });
              break;
            }
          }
        }
      }
      setTypeaheadQuery(e.value || '');
      searchProps.onNeuChange?.(e);
    };

    return (
      <>
        <NeuSearch
          ref={typeaheadRef}
          placeholder={fieldProps.placeholder || 'Enter location'}
          className={clsx('typeahead', searchProps.className, styles.extendedDropdown)}
          autoComplete="off"
          {...searchProps}
          value={typeaheadQuery}
          onNeuChange={handleNeuChange}
          onNeuBlur={(event) => handleBlur(event)}
          onKeyDown={handleKeyDown}
          dropdown
          minchar={shouldShowDropdown ? MIN_SEARCH_CHARACTERS : 999}
          invalid={String(rhfFieldError?.message)}
          error={!!rhfFieldError}
          role="combobox"
          aria-label={t('Search.Result.Results')}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-controls={`${fieldProps.name}-typeahead-listbox`}
          aria-expanded={shouldShowDropdown}
          aria-describedby={`${fieldProps.name}-typeahead-noresults ${fieldProps.name}-typeahead-searchstatus`}
          aria-activedescendant={
            shouldShowDropdown && selectedItem && selectedItem.id !== STORED_LOCATION_ID
              ? `${fieldProps.name}-typeahead-option-${selectedItem.id}`
              : undefined
          }
        >
          {!typeaheadProps.typeaheadLoading && typeaheadItems.length > 0 && (
            <>
              <TypeaheadResults
                typeaheadItems={typeaheadItems}
                query={typeaheadQuery}
                selectedItem={selectedItem}
                closeDropdown={() => clearTypeaheadItems()}
                onSelect={onSelectedItem}
                focusFirstItemRef={focusFirstItemRef}
              />
              <TypeaheadAriaResults typeaheadItems={typeaheadItems} query={typeaheadQuery} />
            </>
          )}
          {!typeaheadProps.typeaheadLoading && typeaheadItems.length < 1 && (
            <TypeaheadNoResults fieldName={searchProps.name} />
          )}
        </NeuSearch>
        <InputSpinner isLoading={typeaheadProps.typeaheadLoading} />
        <Controller
          name={fieldProps.name}
          control={control}
          render={({ field }) => {
            return <input type="hidden" {...field} ref={ref} />;
          }}
        />
      </>
    );
  }
);

TypeaheadNew.displayName = 'NewTypeahead';
